import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { Button } from 'primeng/button';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { TagModule } from 'primeng/tag';
import { AssetAccount, LiabilityAccount } from '../client-accounts.model';

type AnyAccount = AssetAccount | LiabilityAccount;

function isLiability(account: AnyAccount): account is LiabilityAccount {
  return 'apr' in account;
}

const USD = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
const PCT = new Intl.NumberFormat('en-US', { style: 'percent', maximumFractionDigits: 1 });
const APR_FMT = new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const CATEGORY_LABELS: Record<string, string> = {
  checking:        'Checking',
  savings:         'Savings',
  investment:      'Investment',
  retirement:      'Retirement',
  real_estate:     'Real Estate',
  other_asset:     'Other Asset',
  credit_card:     'Credit Card',
  student_loan:    'Student Loan',
  medical_debt:    'Medical Debt',
  personal_loan:   'Personal Loan',
  auto_loan:       'Auto Loan',
  mortgage:        'Mortgage',
  other_liability: 'Other Liability',
};

@Component({
  selector: 'bai-account-detail-dialog',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [Button, TagModule],
  template: `
    <div class="flex flex-col gap-4 p-2" role="region" aria-label="Account details">
      <div class="grid grid-cols-2 gap-x-6 gap-y-3 text-sm">
        <span class="text-gray-500 font-medium">Account Name</span>
        <span class="font-semibold">{{ account.name }}</span>

        <span class="text-gray-500 font-medium">Institution</span>
        <span>{{ account.institution }}</span>

        <span class="text-gray-500 font-medium">Last 4 Digits</span>
        <span class="font-mono">•••• {{ account.last4 }}</span>

        <span class="text-gray-500 font-medium">Account Type</span>
        <span>{{ categoryLabel(account.category) }}</span>

        <span class="text-gray-500 font-medium">Current Balance</span>
        <span class="font-semibold">{{ formatUsd(account.balance) }}</span>

        <span class="text-gray-500 font-medium">YTD Change</span>
        <span [class.text-green-600]="isPositiveYtd()" [class.text-red-600]="isNegativeYtd()">
          {{ formatYtd() }}
        </span>

        <span class="text-gray-500 font-medium">Last Updated</span>
        <span [class.text-amber-600]="isStale()">
          {{ account.lastUpdated }}
          @if (isStale()) {
            <span class="ml-1 text-xs">(stale)</span>
          }
        </span>

        @if (isLiabilityAccount()) {
          @let liab = asLiability();

          @if (liab.apr !== null) {
            <span class="text-gray-500 font-medium">APR</span>
            <span [class.text-red-600]="liab.apr > 20" [class.text-amber-600]="liab.apr > 15 && liab.apr <= 20">
              {{ formatApr(liab.apr) }}%
              @if (liab.apr > 20) {
                <p-tag value="High APR" severity="danger" styleClass="ml-2 text-xs" />
              }
            </span>
          }

          @if (liab.creditLimit !== null) {
            <span class="text-gray-500 font-medium">Credit Limit</span>
            <span>{{ formatUsd(liab.creditLimit) }}</span>
          }

          @if (liab.utilization !== null) {
            <span class="text-gray-500 font-medium">Utilization</span>
            <span [class.text-red-600]="liab.utilization > 0.5" [class.text-amber-600]="liab.utilization > 0.3 && liab.utilization <= 0.5">
              {{ formatPct(liab.utilization) }}
              @if (liab.utilization > 0.5) {
                <p-tag value="High" severity="danger" styleClass="ml-2 text-xs" />
              } @else if (liab.utilization > 0.3) {
                <p-tag value="Elevated" severity="warn" styleClass="ml-2 text-xs" />
              }
            </span>
          }

          @if (liab.minPayment !== null) {
            <span class="text-gray-500 font-medium">Min. Payment</span>
            <span>{{ formatUsd(liab.minPayment) }}</span>
          }

          <span class="text-gray-500 font-medium">Risk Status</span>
          <p-tag [value]="riskLabel(liab.risk)" [severity]="riskSeverity(liab.risk)" />
        }
      </div>

      <div class="flex justify-end pt-2 border-t border-gray-100">
        <p-button label="Close" (onClick)="close()" aria-label="Close account detail dialog" />
      </div>
    </div>
  `,
})
export class AccountDetailDialogComponent {
  private readonly _ref = inject(DynamicDialogRef);
  private readonly _config = inject(DynamicDialogConfig);

  readonly account = this._config.data as AnyAccount;

  isLiabilityAccount(): boolean {
    return isLiability(this.account);
  }

  asLiability(): LiabilityAccount {
    return this.account as LiabilityAccount;
  }

  categoryLabel(category: string): string {
    return CATEGORY_LABELS[category] ?? category;
  }

  formatUsd(value: number): string {
    return USD.format(value);
  }

  formatPct(value: number): string {
    return PCT.format(value);
  }

  formatApr(value: number): string {
    return APR_FMT.format(value);
  }

  formatYtd(): string {
    const d = this.account.ytdChangeDollar;
    const p = this.account.ytdChangePct;
    const sign = d >= 0 ? '+' : '';
    return `${sign}${USD.format(d)} (${sign}${p.toFixed(1)}%)`;
  }

  isPositiveYtd(): boolean {
    const liab = isLiability(this.account);
    return liab ? this.account.ytdChangeDollar < 0 : this.account.ytdChangeDollar > 0;
  }

  isNegativeYtd(): boolean {
    const liab = isLiability(this.account);
    return liab ? this.account.ytdChangeDollar > 0 : this.account.ytdChangeDollar < 0;
  }

  isStale(): boolean {
    const updated = new Date(this.account.lastUpdated);
    return (Date.now() - updated.getTime()) / (1000 * 60 * 60 * 24) > 90;
  }

  riskLabel(risk: string): string {
    const labels: Record<string, string> = {
      healthy: 'Healthy',
      caution: 'Caution',
      risk:    'Risk',
      neutral: 'Neutral',
    };
    return labels[risk] ?? risk;
  }

  riskSeverity(risk: string): 'success' | 'warn' | 'danger' | 'secondary' {
    if (risk === 'healthy') return 'success';
    if (risk === 'caution') return 'warn';
    if (risk === 'risk')    return 'danger';
    return 'secondary';
  }

  close(): void {
    this._ref.close();
  }
}
